import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, useWindowDimensions, TextInput } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ZenIcon, ZenText, ZenToast } from 'zen-core-elements';
import SplitPayment from './splitPayment';
import { ThemeConsumer } from '@rneui/themed';
import { printHtml } from '../../../../platform/printer/printer';
import { Theme } from '@rneui/themed';
import CustomerModal from '../../sellscreenCheckout/addCustomer';
import SinglePayment from './singlePayment';
import PaymentCompleted from './paymentCompleted';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import CommonService from '../../../../shared/services/commonService';
import { InsertBulkProductIntoCartApi, onCompleteSale, placeOrderApi, removeTransactionData, setSplitPaymentComplete, setTotalAmount, toggleSplitPayment } from '../../../../redux/slices/newSellscreenPaymentSlice';
import { AppliedLineDiscountPayload, InsertBulkProductIntoCartPayload, placeOrderPayload } from '../../../sellScreen/sellScreenModule';
import { config } from '../../../../shared/environment';
import moment from 'moment';
import { appColors } from '../../../../themes/appTheme';
import Popover from 'react-native-popover-view';
import CommonDialogBox from '../../../../shared/commonComponents/commonDialogBox';
import { addCustomer, clearCart, resetSale, returnOrderForSellScreen } from '../../../../redux/slices/newSellscreenSlice';
import { useAppNavigation } from '../../../../types/@react-navigation/navigation';
import RefundPayment from './refundPayment';
import { PAYMENT_METHODS } from '../../../../redux/utils/discountUtils';
import { updateRefundAmount, updateReturnProductStatus } from '../../../../redux/slices/orderDetailSlice';
import { updateOrderDetails } from '../../../../redux/slices/orderSlice';
import { encryptString } from 'zen-core-elements';
import { getOrderSettingsFromAsyncStorage } from '../../../../utils/asyncstorageHelper';
import { addCartProductType } from '../../../../redux/types/sellScreenTypes';
import { BuyXGetYDiscount, toMajor } from '../../../../redux/utils/discountEngine';

const SellscreenPayment = ({ isWrite }: { isWrite: boolean }) => {
	const dispatch = useAppDispatch()
	const navigation = useAppNavigation()
	const { width } = useWindowDimensions()
	const { totalAmount, productCount, isSplitPayment, remainingAmount, paymentCompleted, paidAmount, transactionData } = useAppSelector((state) => state.NewSellscreenPaymentSlice)
	const { customerDetails, cartProducts, tip, customerNotes, summary, additionalCharge, cartQuantity, sellScreenType, returnRequestData, returnRequestWholeOrderData, pricedCart, manualDiscount, discountStore } = useAppSelector((state) => state.NewSellscreenSlice)
	const checkoutGiftCart = useAppSelector((state) => state?.sellScreen?.checkoutGiftCart)
	const isBuyingGiftCard = Boolean(checkoutGiftCart?.giftCardValue?.value || checkoutGiftCart?.giftCardValue)
	const { currentLocation: { id: locationId, timeZone }, currentUser: { value: currentUser } } = useAppSelector((state) => state?.common)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const inputRef = useRef<TextInput>(null);
	const [paymentMethod, setPaymentMethod] = useState<typeof PAYMENT_METHODS[0]["id"]>("")
	const [orderIdFormat, setOrderIdFormat] = useState({ orderIdPrefixText: "ZEN", orderIdSuffixText: "ID" })
	const [reason, setReason] = useState<string>('');

	// Load the order ID prefix/suffix configured in Order Settings
	useEffect(() => {
		getOrderSettingsFromAsyncStorage().then(({ orderIdPrefixText, orderIdSuffixText }) => {
			setOrderIdFormat({ orderIdPrefixText, orderIdSuffixText })
		}).catch((error) => {
			console.log("Get order settings error", error)
		})
	}, [])

	useEffect(() => {
		if (checkoutGiftCart?.giftCardValue?.value) {
			dispatch(setTotalAmount({
				total: Number(checkoutGiftCart.giftCardValue.value),
				productCount: 1
			}))
		}
	}, [checkoutGiftCart])

	const [splitPaymentPopover, setSplitPaymentPopover] = useState(false)

	const splitPaymentAlertDialogProps = {
		content: "This transaction has already been processed. Do you want to modify the split payment?",
		button: [{ id: 1, name: "Okay" }, { id: 2, name: "Cancel" }],
		icon: { name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community' }
	}

	const handleSplitPaymentToggle = () => {
		if (customerDetails?.id) {
			if (transactionData?.length > 0) {
				setSplitPaymentPopover(true)
			} else {
				dispatch(toggleSplitPayment())
			}
		} else {
			ZenToast({ type: "warning", message: "Please select a customer to toggle split payment" })
		}
	}

	const handleDisabled = () => {
		if (remainingAmount > 0) {
			return true
		}
		return false
	}

	// Every discount the device applied, with claimed get products, so the server can re-validate them
	const buildDiscountDetails = (): placeOrderPayload["orders"]["discountDetails"] => {
		const priced = sellScreenType === "SELL" ? pricedCart?.[locationId] : null
		const automatic = new Map<number, number[]>()
		priced?.lines?.forEach((line) => {
			if (line.discount?.kind === "AUTOMATIC") automatic.set(line.discount.discountId, [...(automatic.get(line.discount.discountId) ?? []), line.variantId])
		})
		const payXGetFree = new Map<number, { productId: number, variantId: number, quantity: number }[]>()
		priced?.freeLines?.forEach((line) => {
			payXGetFree.set(line.discountId, [...(payXGetFree.get(line.discountId) ?? []), { productId: line.productId, variantId: line.variantId, quantity: line.quantity }])
		})
		return {
			automatic: [...automatic].map(([discountId, variantIds]) => ({ discountId, variantIds })),
			buyXgetY: (priced?.claims ?? []).filter((claim) => claim.status === "APPLIED").map((claim) => {
				const discount = discountStore?.[locationId]?.byId?.[claim.discountId] as BuyXGetYDiscount | undefined
				const option = discount?.get?.options?.find((item) => item.variantId === claim.getVariantId)
				return { discountId: claim.discountId, isCategory: discount?.get?.type === "CATEGORY", getProductId: option?.productId ?? 0, getVariantId: claim.getVariantId, quantity: claim.getUnits }
			}),
			payXGetFree: [...payXGetFree].map(([discountId, freeProducts]) => ({ discountId, freeProducts })),
			coupon: priced?.coupon?.applied ? { discountId: priced.coupon.id, promoCode: manualDiscount?.[locationId]?.promoCode || "" } : null,
		}
	}

	const generatePayload = (): placeOrderPayload => {
		const now = new Date();
		const { subTotal, discountedPrice, additionalDiscount, couponDiscount, otherDiscount } = summary?.[locationId]
		// The order keeps the exact total; the payment records the rounded amount collected (round off = payment - order)
		const exactOrderTotal = sellScreenType === "SELL" ? (summary?.[locationId]?.grandTotal ?? totalAmount) : totalAmount

		const giftCardItem = transactionData?.find((item) => item?._paymentMethod === "Gift Card" || item?.giftCardId)
		const purchasedGiftCardId = giftCardItem?.giftCardId || giftCardItem?._giftCardPayment?.id || 0
		const usedGiftCardAmount = transactionData?.filter((item) => item?._paymentMethod === "Gift Card" || item?.giftCardId)?.reduce((sum, item) => sum + Number(item?._paidAmount || item?.amount || 0), 0) || 0
		const giftCardBalanceAmount = Number(giftCardItem?._giftCardPayment?.currentBalance || 0) - usedGiftCardAmount
		// console.log(JSON.stringify(transactionData, undefined, 4));

		const priced = sellScreenType === "SELL" ? pricedCart?.[locationId] : null
		const appliedClaims = priced?.claims?.filter((claim) => claim.status === "APPLIED") ?? []
		// Every discount on a line, for the server to validate and record (ordered_product_discounts_mapping)
		const buildLineDiscounts = (item: addCartProductType) => {
			if (item?.isFreeGift) {
				const free = priced?.freeLines?.find((line) => line.discountId === item?.freeGiftDiscountId && line.variantId === item?.variantId)
				const value = toMajor((free?.originalUnitPrice ?? 0) * (free?.quantity ?? 1))
				return { actualPrice: toMajor(free?.originalUnitPrice ?? 0), lineDiscount: value, appliedDiscounts: [{ discountId: item?.freeGiftDiscountId!, type: "PAY_X_GET_FREE" as const, amount: value }] }
			}
			const line = priced?.lines?.find((pricedLine) => pricedLine.key === String(item?.variantId))
			const appliedDiscounts: AppliedLineDiscountPayload[] = []
			if (line?.discount) {
				const claim = appliedClaims.find((item) => item.discountId === line.discount!.discountId && item.getVariantId === line.variantId)
				appliedDiscounts.push(line.discount.kind === "AUTOMATIC"
					? { discountId: line.discount.discountId, type: "AUTOMATIC", amount: toMajor(line.discount.amount) }
					: { discountId: line.discount.discountId, type: "BUY_X_GET_Y", amount: toMajor(line.discount.amount), getVariantId: line.variantId, quantity: claim?.getUnits ?? null })
			}
			if (line?.couponShare && priced?.coupon?.applied) {
				appliedDiscounts.push({ discountId: priced.coupon.id, type: "COUPON", amount: toMajor(line.couponShare), promoCode: manualDiscount?.[locationId]?.promoCode || null })
			}
			return { actualPrice: item?.price, lineDiscount: toMajor(line?.discount?.amount ?? 0), appliedDiscounts }
		}
		const toPayloadLine = (item: addCartProductType): placeOrderPayload["orderSummaryData"]["cartData"]["cartProducts"][number] => {
		const lineDiscounts = buildLineDiscounts(item)
		return ({
			"quantity": item?.quantity,
			"id": /* 34571 */-1,
			"combinationName": null,
			"instruction": null,
			"productId": item?.productId,
			"variantId": item?.variantId,
			"createdAt": now.toISOString(),
			"isModifierProduct": item?.isVariants ? true : false,
			"isPos": true,
			"isVariants": item?.isVariants,
			"productSubTotal": item?.finalPrice,
			"price": item?.price,
			"discountAmount": item?.discount || item?.autoDiscount,
			// Fields the placeorder service stores on ordered_products
			// Original price, only for free items (the order details page reads a set actualPrice as "free get item")
			"actualPrice": item?.isFreeGift ? lineDiscounts.actualPrice : null,
			"automaticDiscountPrice": item?.isFreeGift ? 0 : lineDiscounts.lineDiscount,
			"appliedDiscountValue": item?.isFreeGift ? lineDiscounts.lineDiscount : (item?.discount || 0),
			"isFreeProduct": item?.isFreeGift || false,
			"appliedDiscounts": sellScreenType === "SELL" ? lineDiscounts.appliedDiscounts : undefined,
			"variant": {
				"id": item?.variantId,
				"price": item?.price,
				"variantName": item?.variantName || "",
				"weightCount": item?.weightCount,
				"variantAvailability": item?.isVariants,
				"combinationName": item?.name + (item?.variantName || ""),
				"sku": item?.sku,

				"wholeSalePrice": item?.wholeSalePrice || null,
				"weight": Number(item?.weight) || null,
				"weightUnitId": item?.weightUnitId || null,

				"taxCode": "", //no
			},
			"productPrice": item?.price,
			"product": {
				"id": item?.productId,
				"name": item?.name,
				"isVariants": item?.isVariants,
				"weightUnit": item?.weightUnit || null,
				"weightUnitId": item?.weightUnitId || null,
				"featuredImage": item?.featuredImage || null,

				"shippingType": null, //no
				"fixedRateType": null, //no
				"fixedRate": null, //no
				"orderQuantityMinimum": null, //no
				"orderQuantityMaximum": null, //no
				"isReplaceable": false, //no
				"isTaxable": false, //no
				"multiLocationVariantStocks": [
					{
						"id": item?.multiLocationVariantStocksId,
						"variantSkuId": item?.variantSkuId,
						"isStock": item?.stock ? true : false,
						"stock": item?.stock,

						"qtyOnHold": null,//no
						"readyInHours": null,//no
					}
				],
			},

			"isReplaceOrder": null, //no
			"cartId": -1, //no
			// The line discount the device applied (automatic, Buy X Get Y or Pay X Get Free)
			"discountId": item?.isFreeGift ? item?.freeGiftDiscountId ?? null : priced?.lines?.find((line) => line.key === String(item?.variantId))?.discount?.discountId ?? null,
			"discountProductId": null, //no
			"fbtId": null, //no
			"salesDiscountId": null, //no
			"isProductCustomizable": false, //no
			"isFbtDefaultProduct": false, //no
			"posProductName": null, //no
			"posPrice": null, //no
			"isSaveForLater": false, //no
			"isMenuClosed": false, //no

			"cartProductModifiers": [], //no
			"addonImageData": [], //no
			"isInclusiveTax": false, //no
			"taxCode": "P000000", //no
			"brandId": null, //no

			"isCategoryAvailable": true,//no
			"productTaxMapping": [], //no
			"categoryId": [
				3033
			],//no
			"isDisabled": false,//no
			"qtyUpdateAlert": null,//no
			"disableQtyUpdate": false,//no
			"optionsPrice": 0,//no
			"productWithOptionPrice": item?.price,//no
			"optionQty": 0,//no
			"modifierPrice": 0,//no
			"buyXgetYDiscounts": appliedClaims.filter((claim) => claim.getVariantId === item?.variantId).map((claim) => ({
				discountId: claim.discountId,
				getyPercentage: (discountStore?.[locationId]?.byId?.[claim.discountId] as BuyXGetYDiscount | undefined)?.get?.percent ?? 0,
				quantity: claim.getUnits,
				discountAmount: toMajor(claim.amount),
			})),
			// Per unit, like the storefront cart (order details shows (price + tax) x qty)
			"taxAmount": item?.quantity ? Math.round((item?.tax / item?.quantity) * 100) / 100 : 0,
			"appliedInclusiveTaxes": [],//no
			"inclusiveTaxPrice": 0,//no
		})
		}
		// Pay X Get Free items go to the order as their own lines at 0 price and 0 tax
		const freeLineItems: addCartProductType[] = (priced?.freeLines ?? []).map((line) => ({
			name: line.name, variantName: "", price: 0, quantity: line.quantity, variantId: line.variantId, productId: line.productId,
			weightCount: 1, stock: 0, sku: null, upc: null, finalPrice: 0, discount: 0, autoDiscount: 0, tax: 0, isVariants: false,
			wholeSalePrice: null, weight: null, weightUnit: null, weightUnitId: null, featuredImage: null, multiLocationVariantStocksId: 0, variantSkuId: line.variantId,
			isFreeGift: true, freeGiftDiscountId: line.discountId,
		}))
		const cartProductsData = [...(cartProducts?.[locationId] ?? []), ...freeLineItems].map(toPayloadLine)

		const paidStoreCreditAmount = transactionData?.find((item) => item?._paymentMethod == "Store Credit")?.amount || 0
		const onAccountItem = transactionData?.find((item) => item?._paymentMethod === "On Account" || item?.paymentMode === "On Account")
		const onAccountAmount = Number(onAccountItem?._paidAmount || onAccountItem?.amount || 0)
		const calculatedPaidAmount = onAccountItem ? Math.max(0, paidAmount - onAccountAmount) : paidAmount
		const mainPaymentType = onAccountItem ? "On Account" : isSplitPayment ? "Multiple method" : transactionData?.[0]?.paymentMode === "CASH IN" ? "CASH" : transactionData?.[0]?.paymentMode === "STORE CREDIT PAYMENT" ? "STORE CREDIT" : transactionData?.[0]?.paymentMode === "ONLINE PAYMENT" ? "CARD" : transactionData?.[0]?.paymentMode

		return {
			"orders": {
				"isZen": false,
				"isPreorder": false,
				"isBuyNow": false,
				"isManualOrder": false,
				"isQuickCheckout": false,
				"codAmount": 0,
				// "codAmount": totalAmount,
				"tipAmount": tip?.[locationId] || 0,
				"customerId": customerDetails?.customerId!!,
				"locationId": locationId,
				"pickupMethodId": null,
				"planId": currentUser?.planId || "",
				"timeZone": timeZone,
				"currencySymbol": currencySymbol,
				"promoCode": priced?.coupon?.applied ? manualDiscount?.[locationId]?.promoCode || "" : "",
				"phoneNumber": currentUser?.phone || "",
				"languageCode": currentUser?.preferredLanguage || "en",
				"todayDate": moment(now).format("dddd MMMM D, YYYY - h:mm A"),
				"currentDate": now.toString(),
				"pickupTiming": {
					"pickupDate": moment(now).add(1, "day").startOf("day").toDate().toString(),
					"pickupTime": moment(now).format("h:mm A")
				},
				"customerNotes": customerNotes?.[locationId] || "",
				"tip": {
					"type": "Amount",
					"amount": tip?.[locationId] || 0
				},
				"paymentDetails": {
					"type": mainPaymentType
				},
				"shippingType": "PICKUP",
				"paymentMethodType": mainPaymentType,
				"shippingMethodType": "PICKUP",
				"taxDetails": [],
				"calculationJson": {
					"pickup": {}
				},
				"deliveryAddress": {},
				"availableValueToRedeem": null,
				"marketingType": null,
				"saveCard": null,
				"campaignId": null,
				"cartProduct": null,
				"expectedDate": null,

				"isLoyaltyPointsApplied": false,
				"discountDetails": buildDiscountDetails(),
				"giftCardCoupenCode": giftCardItem?._giftCardPayment?.couponCode || null,
			},
			"paymentDetails": {
				"storeId": config?.storeId,
				"isSplitPayment": isSplitPayment,
				"status": isSplitPayment ? onAccountItem ? "PARTIALLY_PAID" : "PAID" : onAccountItem ? "PENDING" : "PAID",
				"customerId": customerDetails?.id!!,
				"balance": remainingAmount + paidStoreCreditAmount + onAccountAmount,
				"paidAmount": calculatedPaidAmount,
				"totalAmount": totalAmount,
				"transactionData": transactionData ? transactionData?.map((item) => ({
					amount: item?._paidAmount,
					storeId: config?.storeId,
					giftCardId: item?.giftCardId || null,
					paymentMode: item?.paymentMode,
					paymentStatus: item?.storeCreditId ? "PENDING" : item?.paymentStatus,
					reason: item?.reason || "",
					storeCreditId: item?.storeCreditId || null
				})) : []
			},
			"customerDetails": customerDetails,
			"orderSummaryData": {
				"cartData": {
					"id": -1,
					"customerId": customerDetails?.customerId!!,
					"cartProducts": cartProductsData
				},
				"giftCardDetails": {
					"purchasedGiftCardId": purchasedGiftCardId,
					"giftCardBalanceAmount": giftCardBalanceAmount,
					"usedGiftCardAmount": usedGiftCardAmount
				},
				"orderSettings": {
					"id": 86,
					"orderHandlingFeesType": "Fixed",
					"orderHandlingFeesAmount": 0,
					"orderExpirationWarnBefDays": 1,
					"isEditOrders": true,
					"isHoldAmount": false,
					"holdAmountType": "Percentage",
					"holdAmount": 0,
					"isAdditionalCharge": false,
					"isCartInstruction": false,
					"shippingRefund": true,
					"storeId": config?.storeId,
					"tips": true,
					"maxDiscount": null,
					"allowAutomaticDiscount": false,
					"allowManualDiscount": false,
					"allowMultipleAutomaticDiscount": false,
					"isManualOrderAllowed": true,
					"allowGuestUsers": true,
					"allowTipPercentage": false,
					"returnActions": "",
					"returnReasons": "",
					"isCodCharge": false,
					// "isCodCharge": true,
					"isDeleted": false,
					"createdAt": "2026-01-14T06:29:27.630Z",
					"modifiedAt": "2026-02-06T12:59:39.686Z",
					"orderExpirationDays": 1,
					"additionalChargeType": "Fixed",
					"tipMandatory": true,
					"orderIdPrefixText": orderIdFormat.orderIdPrefixText,
					"orderIdSuffixText": orderIdFormat.orderIdSuffixText,
					"additionalCharge": 0,
					"additionalChargeLabel": "",
					"codAmount": 0,
					// "codAmount": totalAmount,
					"codChargeType": "Fixed",
					"codAdvanceCollectionType": "BOTH",
					"codAdvanceCollectionValue": 0,
					// "codAdvanceCollectionValue": totalAmount,
					"isCodChargeRefund": false,
					// "isCodChargeRefund": true,
					"isCodAdvanceCollection": false
					// "isCodAdvanceCollection": true
				},
				"overAllSummary": {
					"subTotal": subTotal,
					"holdAmount": 0,
					"discount": subTotal - discountedPrice,
					"tipAmount": tip?.[locationId] || 0,
					"taxAmount": summary?.[locationId]?.tax || 0,
					"serviceCharge": 0,
					"shipping": 0,
					"handlingFee": 0,
					"additionalCharge": additionalCharge?.[locationId]?.amount || 0,
					"totalAmount": exactOrderTotal,
					"productQty": cartQuantity?.[locationId],
					"depositAmount": 0,
					"postPaymentCodAmount": 0,
					"prePaymentCodAmount": 0,
					"isManualDiscountApplicable": true,
					"orderDiscount": subTotal - discountedPrice,
					"orderAmount": exactOrderTotal,
					"taxDetails": [],
					"inclusiveTaxDetails": [],
					"avalaraTaxError": null,
					"taxCode": null,
					"taxPluginId": null,
					"inclusiveTaxAmount": 0,
					"availableValueToRedeem": 0,
					"loyaltyData": {
						"balancePoints": 500,
						"balanceValue": 0,
						"storeRedeemPointsId": 220,
						"availablePointsToRedeem": 10,
						"availableValueToRedeem": 10,
						"balancePointsToRedeem": 0,
						"balanceValueToRedeem": 0,
						"storeEarnPointsId": 438,
						"afterFulfilment": false,
						"earningPoints": 100,
						"settingsData": {
							"general": {
								"excludeDiscountedOrder": false,
								"excludeGiftCardOrder": false,
								"excludeShipping": false,
								"excludeTax": false
							},
							"redeemSettings": {
								"id": 220,
								"minPurchaseValue": null,
								"redeemPointsType": 2583,
								"noOfPoints": 1,
								"redeemValue": 1,
								"storeId": config.storeId,
								"pointsTypeId": 45,
								"isActive": true,
								"isDeleted": false
							},
							"earnSettings": {
								"id": 438,
								"perSpent": 1,
								"noOfPoints": 10,
								"earningType": 2554,
								"isOrderComplete": false
							}
						}
					},
					"posAdditionalDiscount": additionalDiscount || 0,
					"isGiftcardPluginEnabled": true,
					"additionalDiscount": additionalDiscount || 0,
					"couponDiscount": couponDiscount,
					"otherDiscount": otherDiscount,
					"discountedPrice": discountedPrice,
					"overAllQuantity": cartQuantity?.[locationId]
				}
			}
		}
	}

	const insertBulkProductIntoCart = () => {
		const payload: InsertBulkProductIntoCartPayload["cartProducts"] = []
		cartProducts?.[locationId]?.map((item) => {
			payload.push({
				productId: item?.productId,
				variantId: item?.variantId,
				combinationName: item?.variantName || null,
				quantity: item?.quantity,
				instruction: null
				// price: item?.price,
				// discount: item?.discount,
				// tax: item?.tax
			})
		})
		dispatch(InsertBulkProductIntoCartApi({
			customerId: customerDetails?.id!!,
			storeId: config.storeId,
			payload: {
				cartProducts: payload
			}
		})).unwrap().then((res) => {
			console.log("res", res)
		}).catch((err) => {
			console.log("err", err)
		})
	}

	const handleSave = () => {
		if (customerDetails?.id) {
			if (paymentCompleted) {
				if (isBuyingGiftCard) {
					dispatch(onCompleteSale());
					dispatch(resetSale());
					navigation?.goBack();
					ZenToast({ message: "Gift card order placed successfully", type: "success" })
				} else {
					const payload = generatePayload()
					// console.log(JSON.stringify(payload, undefined, 4));
					dispatch(placeOrderApi({ payload, storeId: config.storeId }))
				}
			} else {
				dispatch(setSplitPaymentComplete())
			}
		} else {
			ZenToast({ type: "warning", message: "Please choose a customer to continue with the payment." })
		}
	}




	const splitPaymentConfirmation = (item: { id: number }) => {
		setSplitPaymentPopover(false)
		if (item?.id == 1) {
			dispatch(toggleSplitPayment())
		}
	}

	const onPaymentClose = () => {
		setPaymentMethod('')
		inputRef?.current?.blur()
	}

	const updateOrderDetailsApi = async (isRestock = false) => {
		let data;
		if (isRestock) {
			const returnWholeStockData = returnRequestWholeOrderData?.map(item => ({
				"status": "Automatic return",
				"variantSkuId": item?.variantSkuId,
				"combinationName": item?.combinationName,
				"quantity": item?.quantity,
				"productId": item?.productId,
				"locationId": item?.locationId,
				"orderProductId": item?.orderProductId
			}))

			const returnStockForSpecificProduct = [
				{
					"status": "Automatic return",
					"variantSkuId": returnRequestData?.variantId,
					"combinationName": returnRequestData?.combinationName,
					"quantity": returnRequestData?.quantity,
					"productId": returnRequestData?.productId,
					"locationId": returnRequestData?.locationId,
					"orderProductId": returnRequestData?.orderProductId
				}
			]

			data = {
				customizedOrderId: returnRequestData?.customizedOrderId ?? null,
				isPreOrder: false,
				modifiedAt: new Date(),
				orderId: returnRequestData?.orderId!,
				orderStatusDate: new Date(),
				storeId: config.storeId,
				userEmail: customerDetails?.email!,
				returnedStock: returnRequestData?.isReturnWholeOrder ? returnWholeStockData : returnStockForSpecificProduct
			}
		} else {
			data = {
				customizedOrderId: returnRequestData?.customizedOrderId ?? null,
				event: "statusChange",
				isPreOrder: false,
				modifiedAt: new Date(),
				orderId: returnRequestData?.orderId!,
				orderName: returnRequestData?.orderName!,
				orderStatus: returnRequestData?.orderStatus!,
				orderStatusCode: returnRequestData?.orderStatusCode!,
				orderStatusDate: new Date(),
				storeId: config.storeId,
				todayDate: moment(new Date()).format('MMMM D, YYYY - h:mm A'),
				userEmail: customerDetails?.email!
			}
		}
		await dispatch(updateOrderDetails({ storeId: config.storeId, orderId: returnRequestData?.orderId, data })).unwrap().catch((err) => {
			ZenToast({ message: "Error while updating order details", type: "error" })
		})
	}
	console.log("returnRequestData", returnRequestData)
	const updateReturnStatus = async () => {
		const data = {
			"todayDate": moment(new Date).format('MMMM DD, YYYY - hh:mm A'),
			"status": "Returned",
			"statusChange": "statusChange",
			"email": customerDetails?.email,
			"orderId": returnRequestData?.orderId,
			"productId": returnRequestData?.productId,
			"orderProductId": returnRequestData?.orderProductId,
			"combinationName": returnRequestData?.combinationName,
			"variantId": returnRequestData?.variantId ? returnRequestData?.variantId : null,
			"productName": returnRequestData?.productName,
			"userName": returnRequestData?.userName,
			"customerId": customerDetails?.customerId,
			"quantity": returnRequestData?.quantity,
			"imageUrl": returnRequestData?.imageUrl,
			"action": returnRequestData?.action,
			"reason": returnRequestData?.reason,
			"comments": returnRequestData?.comments ? returnRequestData?.comments : null,
			"selectedModifiers": returnRequestData?.selectedModifiers,
			"customizedOrderId": returnRequestData?.customizedOrderId,
			"languageCode": returnRequestData?.languageCode
		}
		const payload = { storeId: config.storeId, orderId: returnRequestData?.orderId!, returnId: returnRequestData?.id!, data: data }
		await dispatch(updateReturnProductStatus(payload)).unwrap().catch((err) => {
			ZenToast({ message: "Error while updating return status", type: "error" })
		})
	}

	const handleRefundAmount = async () => {
		const data = {
			orders: {
				amountPaid: encryptString({ inputString: String(summary?.[locationId]?.grandTotal), key: config.secretKey }),
				customerId: customerDetails?.customerId ?? '',
				phoneNumber: customerDetails?.phone ?? '',
			},
			refund: {
				orderId: String(returnRequestData?.orderId),
				refundAmount: encryptString({ inputString: String(summary?.[locationId]?.grandTotal), key: config.secretKey }),
				refundReason: returnRequestData?.reason ?? '',
				refundType: 'Cash Refund',
				customizedOrderId: returnRequestData?.customizedOrderId,
			},
		};
		await dispatch(updateRefundAmount({ storeId: config.storeId, data: data })).unwrap()
			.then(() => ZenToast({ message: "Amount refunded successfully", type: "success" }))
			.catch((err) => {
				ZenToast({ message: "Error while updating refund amount", type: "error" })
			});
	}

	const handleReturnOrder = async () => {
		console.log("inside the handle return order");
	}

	const handleRefund = async () => {
		// if (!returnRequestData?.isReturnWholeOrder) {
		// 	updateReturnStatus()
		// }

		// updateOrderDetailsApi().then(() => {
		// 	if (sellScreenType === "RETURN") {
		// 		updateOrderDetailsApi(true).then(() => {
		// 			handleRefundAmount()
		// 		})
		// 	} else {
		// 		ZenToast({ message: "Product Exchanged successfully", type: "success" })
		// 	}
		// })
		// await handleReturnOrder()
		const payload = {
			locationId: returnRequestData?.locationId,
			paymentMode: "CASH IN",
			reason: returnRequestData?.reason ?? "",
			returnItems: [
				{
					"orderProductId": returnRequestData?.orderProductId,
					"productId": returnRequestData?.productId,
					"variantId": returnRequestData?.variantId,
					"returnQty": returnRequestData?.quantity,
					"productPrice": returnRequestData?.productPrice,
					"reason": returnRequestData?.reason,
					"comments": reason,
					"stockRestore": true
				}
			]
		}

		dispatch(returnOrderForSellScreen({ storeId: config.storeId, orderId: returnRequestData?.orderId!, payload })).unwrap().then((returnRes) => {
			console.log(returnRes);
			dispatch(clearCart())
			dispatch(addCustomer({
				customerDetails: {
					details: null, locationId: config?.storeId
				}
			}))
			navigation.goBack()
			setPaymentMethod('')
		})
	}

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<SafeAreaView style={[styles.rightPanel, width < 600 && { padding: 10 }]}>
						{paymentCompleted ?
							<PaymentCompleted />
							:
							<View style={{ flex: 1 }}>
								<View style={styles.topBar}>
									{/* Top Left: Financial Summary */}
									<View style={styles.topLeft}>
										<ZenText content="Amount Payable" style={styles.label} />
										<ZenText content={CommonService?.currencyStandard(currencySymbol, totalAmount.toFixed(2))} style={styles.mainAmount} />
										<View style={styles.subInfoRow}>
											<View style={styles.tag}><ZenText content={`${productCount} ${productCount > 1 ? "Items" : "Item"}`} style={styles.tagText} /></View>
											{/* {customerDetails && !isBuyingGiftCard ? <ZenText content={`On-Account Balance: ${CommonService.currencyStandard("₹", customerDetails?.onAccountDetails?.balance?.toString())}`} style={styles.storeCreditText} /> : null} */}
										</View>
									</View>
									<CustomerModal isWrite={isWrite} isPaymentScreen />
								</View>

								<View style={styles.divider} />

								<View style={styles.mainBody}>
									{/* Split Payment Toggle */}
									{sellScreenType !== "RETURN" && <View style={styles.sectionHeader}>
										<ZenText content={isSplitPayment ? "Split Payment" : "Payment Methods"} style={styles.sectionTitle} />
										<TouchableOpacity
											onPress={handleSplitPaymentToggle}
											style={[styles.splitBtn, isSplitPayment && styles.splitBtnActive]}
										>
											<ZenIcon name={isSplitPayment ? "call-split" : "square-outline"} type="material-community" size={20} color={isSplitPayment ? "#fff" : "#64748b"} />
											<ZenText content="Split Payment" style={[styles.splitBtnText, isSplitPayment && styles.textWhite]} />
										</TouchableOpacity>
									</View>}

									{sellScreenType === "RETURN" ? <RefundPayment
										paymentMethod={paymentMethod}
										totalAmount={totalAmount}
										customerDetails={customerDetails}
										onClose={onPaymentClose}
										handleRefund={handleRefund}
										setReason={setReason}
										reason={reason}
									/> : isSplitPayment ? <SplitPayment /> :
										<SinglePayment />}
								</View>
							</View>}

						{/* Bottom Final Action */}
						{sellScreenType !== "RETURN" && (paymentCompleted || isSplitPayment) && <View style={styles.footer}>
							<TouchableOpacity
								style={[styles.completeBtn, handleDisabled() && styles.disabledBtn]}
								disabled={handleDisabled()}
								onPress={handleSave}
							>
								<ZenText content={paymentCompleted ? "Complete Sale" : "Continue"} style={[styles.completeBtnText, handleDisabled() && { color: theme?.disableTextColor }]} />
								{/* {handleDisabled() ? <></> : <ZenIcon name="arrow-right" type="material-community" size={20} color={theme?.light} />} */}
							</TouchableOpacity>
						</View>}

						{/* For uncheck split payment confirmation */}
						<Popover isVisible={splitPaymentPopover} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }} onRequestClose={() => { setSplitPaymentPopover(false) }}>
							<CommonDialogBox type="OTHER" dialog={splitPaymentAlertDialogProps} confirmation={splitPaymentConfirmation} />
						</Popover>
					</SafeAreaView>
				)
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) => StyleSheet.create({
	rightPanel: { flex: 1, backgroundColor: theme?.light, paddingHorizontal: 16, paddingBottom: 16, paddingTop: 15 },

	// Top Bar 
	topBar: { flexDirection: 'row', paddingHorizontal: 16, justifyContent: 'space-between', alignItems: "center" },
	topLeft: { flex: 1 },
	label: { fontSize: theme?.miniFontSize, fontFamily: theme?.medium, color: theme?.grey, textTransform: 'uppercase', letterSpacing: 1 },
	mainAmount: { fontSize: theme?.bigFontSize + 3, fontFamily: theme?.bold, color: theme?.lightDark, marginVertical: 4 },
	subInfoRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
	tag: { backgroundColor: theme?.softSlate, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4 },
	tagText: { fontSize: theme?.miniFontSize, fontFamily: theme?.medium, color: theme?.grey },
	storeCreditText: { fontSize: theme?.smallHeaderFontSize, fontFamily: theme?.bold, color: theme?.primary },
	divider: {
		height: 1,
		backgroundColor: theme.softSlate,
		marginTop: 15,
	},

	// Body Content
	mainBody: { padding: 24, flex: 1 },
	sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
	sectionTitle: { fontSize: theme?.smallerFontSize, fontFamily: theme?.bold, color: theme?.primary },
	splitBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, paddingHorizontal: 16, borderRadius: 12, borderWidth: 1, borderColor: theme?.coolGray },
	splitBtnActive: { backgroundColor: theme?.primary, borderColor: theme?.primary },
	splitBtnText: { fontSize: theme?.smallHeaderFontSize, fontFamily: theme?.bold, color: theme?.lightDark },
	textWhite: { color: theme?.light },

	// Footer
	footer: { padding: 24, borderTopWidth: 1, borderColor: theme?.softSlate },
	completeBtn: { backgroundColor: theme?.primary, flexDirection: 'row', padding: 15, borderRadius: 15, justifyContent: 'center', alignItems: 'center', gap: 12 },
	disabledBtn: { backgroundColor: theme?.disableGray },
	completeBtnText: { color: theme?.light, fontSize: theme?.smallerFontSize, fontFamily: theme?.bold },
});

export default SellscreenPayment;


